
import mapboxgl from 'mapbox-gl';
import { City } from '../../../../data/types/city-types';
import { formatCurrencyShort } from '../../../../data/utils/money-utils';
import { getPopupContent } from './popup-creation';

export const createCityMarker = (
  city: City,
  isSelected: boolean,
  onCityClick: (city: City) => void,
  showFunding: boolean = true
): mapboxgl.Marker => {
  // Create marker container
  const el = document.createElement('div');
  el.className = `city-marker ${isSelected ? 'selected' : ''}`;
  
  // Set data attribute for presence (used for toggle visibility)
  el.setAttribute('data-presence', city.church_presence || 'Unknown');
  
  // Create a pin element for the city itself that will remain visible
  const pinEl = document.createElement('div');
  pinEl.className = 'city-pin';
  pinEl.setAttribute('data-presence', city.church_presence || 'Unknown');
  pinEl.setAttribute('data-marker-type', 'city-pin');
  
  // Determine city type and add appropriate class
  if (city.church_presence === 'Unreached') {
    el.classList.add('city-presence-unreached');
    pinEl.classList.add('pin-unreached');
  } else if (city.church_presence === 'Low') {
    el.classList.add('city-presence-low');
    pinEl.classList.add('pin-low');
  } else if (city.population && city.population > 50000 && city.funding_amount && city.funding_amount < 10000) {
    // Areas of exceptional need: large population with little funding
    el.classList.add('city-presence-need');
    pinEl.classList.add('pin-need');
  }
  
  // Force visibility on pins that should remain visible
  pinEl.style.cssText = `
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: currentColor;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    opacity: 1 !important;
    visibility: visible !important;
    display: block !important;
  `;
  
  el.appendChild(pinEl);
  
  // Add region-specific data attributes if the city is in a special region
  if (city.region_id === 'CHIS') {
    el.setAttribute('data-special-region', 'CHIS');
    el.classList.add('chiapas-city');
    pinEl.classList.add('chiapas-pin');
  } else if (city.region_id === 'HGO') {
    el.setAttribute('data-special-region', 'HGO');
    el.classList.add('hidalgo-city');
    pinEl.classList.add('hidalgo-pin');
  }
  
  // Create funding label positioned above the marker point if funding should be shown
  if (showFunding) {
    const fundingEl = document.createElement('div');
    fundingEl.className = 'hover-wrapper city-marker-funding funding-label';
    fundingEl.textContent = city.funding_amount ? formatCurrencyShort(city.funding_amount) : '$0';
    
    // Add data attributes for hover card to use
    fundingEl.setAttribute('data-city-id', city.city_id || city.id || '');
    fundingEl.setAttribute('data-city-name', city.name);
    fundingEl.setAttribute('data-funding', city.funding_amount?.toString() || '0');
    fundingEl.setAttribute('data-pledges', city.pledges?.toString() || '0');
    fundingEl.setAttribute('data-churches', city.churches_count?.toString() || '0');
    fundingEl.setAttribute('data-missionaries', city.missionaries?.toString() || '0');
    fundingEl.setAttribute('data-population', city.population?.toString() || '0');
    fundingEl.setAttribute('data-presence', city.church_presence || '');
    fundingEl.setAttribute('data-marker-type', 'funding');
    
    if (city.funding_percentage !== undefined) {
      fundingEl.setAttribute('data-funding-percentage', city.funding_percentage.toString());
    }
    
    // Force immediate visibility with stronger styling
    fundingEl.style.cssText = `
      transition: none !important;
      animation: none !important;
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
      z-index: 100 !important;
    `;
    
    el.appendChild(fundingEl);
  }
  
  // Create name element (shows on hover)
  const nameEl = document.createElement('div');
  nameEl.className = 'city-marker-name';
  nameEl.textContent = city.name;
  nameEl.style.display = isSelected ? 'block' : 'none'; // Show immediately if selected
  
  // Force visibility on selected cities
  if (isSelected) {
    nameEl.style.opacity = '1';
    nameEl.style.visibility = 'visible';
  }
  
  // Append name element to marker
  el.appendChild(nameEl);
  
  // Create popup
  const popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: false,
    maxWidth: '300px',
    className: 'city-popup'
  }).setHTML(getPopupContent(city));
  
  // Create the marker
  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom',
    offset: [0, 0],
    pitchAlignment: 'viewport',
    rotationAlignment: 'viewport'
  })
    .setLngLat(city.coordinates as [number, number])
    .setPopup(popup);
  
  // Add hover effects
  el.addEventListener('mouseenter', () => {
    nameEl.style.display = 'block';
    nameEl.style.opacity = '1';
  });
  
  el.addEventListener('mouseleave', () => {
    if (!isSelected) {
      nameEl.style.display = 'none';
      nameEl.style.opacity = '0';
    }
  });
  
  // Add click handlers
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    console.log("City marker clicked:", city.name);
    onCityClick(city);
    marker.togglePopup();
  });
  
  // Initialize custom hover functionality
  initializeHoverCard(el, city);
  
  return marker;
};

// Helper function to initialize the hover card functionality
function initializeHoverCard(element: HTMLElement, city: City) {
  const hoverTrigger = element.querySelector('.hover-wrapper');
  
  if (hoverTrigger) {
    // Add hover classes for styling
    hoverTrigger.classList.add('city-hover-trigger');
    
    // Create hover content that will appear on hover
    const hoverContent = document.createElement('div');
    hoverContent.className = 'city-hover-content';
    
    // Build the hover card content with updated color
    hoverContent.innerHTML = `
      <div class="flex flex-col gap-2 p-3 bg-white/95 backdrop-blur-sm rounded-md shadow-md border border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="font-medium">${city.name}</h3>
          <span 
            class="px-2 py-0.5 rounded-full text-xs text-white"
            style="background-color: ${getPresenceColor(city.church_presence || '')}"
          >
            ${city.church_presence || 'Unknown'}
          </span>
        </div>
        
        <div class="grid grid-cols-2 gap-2 text-sm">
          <div class="flex flex-col">
            <span class="text-gray-500">Funding</span>
            <span class="font-medium text-[#0E4D66]">${formatCurrencyShort(city.funding_amount || 0)}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-gray-500">Pledges</span>
            <span class="font-medium text-[#0E4D66]">${formatCurrencyShort(city.pledges || 0)}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-gray-500">Churches</span>
            <span class="font-medium">${city.churches_count || 0}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-gray-500">Missionaries</span>
            <span class="font-medium">${city.missionaries || 0}</span>
          </div>
        </div>
        
        ${city.population ? `
          <div class="mt-1 text-sm">
            <span class="text-gray-500">Population: </span>
            <span class="font-medium">${city.population.toLocaleString()}</span>
          </div>
        ` : ''}
        
        ${city.funding_percentage !== undefined ? `
          <div class="w-full bg-gray-200 rounded-full h-1.5 mt-1">
            <div 
              class="bg-[#1EAEDB] h-1.5 rounded-full"
              style="width: ${city.funding_percentage}%"
            />
          </div>
        ` : ''}
      </div>
    `;
    
    // Add the hover content to the marker
    element.appendChild(hoverContent);
    
    // Show/hide hover content on mouse events
    hoverTrigger.addEventListener('mouseenter', () => {
      hoverContent.classList.add('active');
    });
    
    hoverTrigger.addEventListener('mouseleave', () => {
      hoverContent.classList.remove('active');
    });
  }
}

// Helper function to get color based on church presence
function getPresenceColor(presence: string): string {
  switch (presence?.toLowerCase()) {
    case 'unreached':
      return '#EF4444'; // Red
    case 'low':
      return '#F97316'; // Orange
    case 'medium':
      return '#FACC15'; // Yellow
    case 'high':
    case 'good':
      return '#22C55E'; // Green
    default:
      return '#94A3B8'; // Gray (default)
  }
}
